import React, { useEffect, useMemo, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { BarChart3, Home, LogOut, PieChart as PieIcon, TrendingDown, TrendingUp } from 'lucide-react'
import { db } from './firebase'

const clampChange = (change) => {
  const numeric = Number.isFinite(change) ? change : 0
  return Math.max(-10, Math.min(10, numeric))
}

const mixColor = (from, to, t) => {
  const start = from.match(/\w\w/g).map((v) => parseInt(v, 16))
  const end = to.match(/\w\w/g).map((v) => parseInt(v, 16))
  const blended = start.map((sv, idx) => Math.round(sv + (end[idx] - sv) * t))
  return `#${blended.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

const getSliceColor = (change) => {
  const clamped = clampChange(change)
  const intensity = Math.pow(Math.abs(clamped) / 10, 0.6)
  if (clamped > 0) {
    return mixColor('bbf7d0', '15803d', intensity) // light to deep green
  }
  if (clamped < 0) {
    return mixColor('fecdd3', 'b91c1c', intensity) // light to deep red
  }
  return '#e5e7eb'
}

const polarToCartesian = (centerX, centerY, radius, angle) => ({
  x: centerX + radius * Math.cos(angle),
  y: centerY + radius * Math.sin(angle)
})

const describeArc = (startAngle, endAngle, radius, center) => {
  const start = polarToCartesian(center, center, radius, startAngle)
  const end = polarToCartesian(center, center, radius, endAngle)
  const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0
  return `M ${center} ${center} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`
}

const buildSlices = (holdings) => {
  const enriched = holdings
    .map((h) => ({
      ...h,
      value: (h.price || 0) * (h.quantity || 0)
    }))
    .filter((h) => h.value > 0)

  const totalValue = enriched.reduce((sum, h) => sum + h.value, 0)
  if (!totalValue) return []

  let current = -Math.PI / 2
  return enriched.map((holding) => {
    const angle = (holding.value / totalValue) * Math.PI * 2
    const slice = {
      ...holding,
      start: current,
      end: current + angle
    }
    slice.path = describeArc(slice.start, slice.end, 140, 180)
    slice.color = getSliceColor(holding.change)
    slice.valueShare = holding.value / totalValue
    current += angle
    return slice
  })
}

function PortfolioPage({ onNavigate, user, onLogout }) {
  const [holdings, setHoldings] = useState([])
  const [loading, setLoading] = useState(true)
  const userInitial = user?.displayName?.[0]?.toUpperCase() || 'N'

  useEffect(() => {
    let mounted = true
    const loadHoldings = async () => {
      if (!user?.uid) {
        setLoading(false)
        return
      }
      try {
        const snapshot = await getDoc(doc(db, 'users', user.uid))
        if (!mounted) return
        const data = snapshot.exists() ? snapshot.data() : null
        const saved = data?.lastPortfolio?.items
        if (Array.isArray(saved)) {
          setHoldings(saved)
        }
      } catch (err) {
        console.warn('Could not load saved portfolio:', err)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    loadHoldings()
    return () => {
      mounted = false
    }
  }, [user])

  const totalValue = useMemo(
    () => holdings.reduce((sum, h) => sum + (h.price || 0) * (h.quantity || 0), 0),
    [holdings]
  )

  const totalChange = useMemo(() => {
    if (!totalValue) return 0
    const weightedChange = holdings.reduce(
      (sum, h) => sum + (h.change || 0) * (h.price || 0) * (h.quantity || 0),
      0
    )
    return weightedChange / totalValue
  }, [holdings, totalValue])

  const slices = useMemo(() => buildSlices(holdings), [holdings])
  const isPositive = totalChange >= 0

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <nav className="relative z-10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={() => onNavigate && onNavigate('home')}
            className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 via-fuchsia-600 to-purple-500 flex items-center gap-2"
          >
            <Home className="w-6 h-6" />
            Neo
          </button>
          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden sm:flex items-center gap-2 bg-white border border-purple-100 rounded-full px-3 py-1 shadow-sm">
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="w-8 h-8 rounded-full border border-purple-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white font-bold flex items-center justify-center">
                    {userInitial}
                  </div>
                )}
                <div>
                  <p className="text-xs text-purple-500 leading-none">Signed in</p>
                  <p className="text-sm text-slate-900 font-semibold leading-none truncate max-w-[140px]">
                    {user.displayName || user.email}
                  </p>
                </div>
              </div>
            )}
            <button
              onClick={() => onNavigate && onNavigate('bubble')}
              className="hidden sm:inline-flex items-center gap-2 text-purple-700 hover:text-purple-900 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              Bubble Chart
            </button>
            <button
              onClick={() => onNavigate && onNavigate('portfolio')}
              className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-4 py-2 rounded-lg hover:shadow-lg hover:shadow-purple-200 transition"
            >
              Portfolio
            </button>
            {onLogout && (
              <button
                onClick={onLogout}
                className="flex items-center gap-2 bg-white border border-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:shadow-md transition"
              >
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            )}
          </div>
        </div>
      </nav>

      <section className="px-6 py-12">
        <div className="max-w-6xl mx-auto space-y-10">
          <header className="flex flex-col gap-3">
            <div className="inline-flex items-center gap-2 text-sm text-purple-700 bg-purple-50 px-3 py-1 rounded-full w-fit border border-purple-100">
              <PieIcon className="w-4 h-4" />
              Portfolio Overview
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">
              Institutional-grade clarity for your holdings
            </h1>
            <p className="text-slate-700 max-w-3xl">
              Track allocation, total value, and performance in a clean view trusted by professional investors. Colors deepen with conviction: greens for gains, reds for drawdowns.
            </p>
          </header>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm">
              <p className="text-sm text-slate-600 mb-2">Total Value</p>
              <p className="text-3xl font-bold">
                ${totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </p>
              <p className="text-xs text-slate-500 mt-1">Based on saved holdings</p>
            </div>
            <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm">
              <p className="text-sm text-slate-600 mb-2">Total Change (weighted)</p>
              <div className={`text-3xl font-bold flex items-center gap-2 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
                {`${isPositive ? '+' : ''}${totalChange.toFixed(1)}%`}
              </div>
              <p className="text-xs text-slate-500 mt-1">Weights by position size</p>
            </div>
            <div className="bg-white border border-purple-100 rounded-2xl p-5 shadow-sm">
              <p className="text-sm text-slate-600 mb-2">Holdings tracked</p>
              <p className="text-3xl font-bold">{holdings.length}</p>
              <p className="text-xs text-slate-500 mt-1">Synced from your latest saved portfolio</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white border border-purple-100 rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-purple-700 font-semibold">
                  <PieIcon className="w-5 h-5 text-purple-500" />
                  Allocation by position
                </div>
                {loading && <span className="text-xs text-slate-500">Loading...</span>}
              </div>

              {holdings.length === 0 ? (
                <div className="text-slate-600 text-sm space-y-3">
                  <p>No saved holdings yet.</p>
                  <button
                    onClick={() => onNavigate && onNavigate('bubble')}
                    className="inline-flex items-center gap-2 text-purple-700 hover:text-purple-900"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Build your portfolio in the Bubble Chart
                  </button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-[1fr] gap-6">
                  <div className="flex justify-center">
                    <svg viewBox="0 0 360 360" className="w-72 h-72 drop-shadow-lg">
                      <circle cx="180" cy="180" r="150" fill="url(#bgGradient)" opacity="0.15" />
                      <defs>
                        <linearGradient id="bgGradient" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#a855f7" />
                          <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                      </defs>
                      {slices.map((slice, idx) => {
                        const sliceChange = Number.isFinite(slice.change) ? slice.change : 0
                        return (
                          <path
                            key={slice.id || slice.name || idx}
                            d={slice.path}
                            fill={slice.color}
                            stroke="#0f172a"
                            strokeWidth="2"
                          >
                            <title>
                              {slice.name} • ${(slice.value || 0).toLocaleString()} • {sliceChange >= 0 ? '+' : ''}
                              {sliceChange.toFixed(1)}%
                            </title>
                          </path>
                        )
                      })}
                      <text x="180" y="175" textAnchor="middle" className="text-xl font-semibold fill-purple-700" aria-hidden="true">
                        {`${isPositive ? '+' : ''}${totalChange.toFixed(1)}%`}
                      </text>
                      <text x="180" y="195" textAnchor="middle" className="text-xs fill-slate-500" aria-hidden="true">
                        total change
                      </text>
                    </svg>
                  </div>
                  <div className="space-y-3">
                    {slices.map((slice, idx) => (
                      <div
                        key={slice.id || slice.name || idx}
                        className="flex items-center justify-between bg-white border border-purple-100 rounded-xl px-4 py-3 shadow-sm"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: slice.color }} />
                          <div>
                            <p className="font-semibold">{slice.name}</p>
                            <p className="text-xs text-slate-500">
                              {slice.quantity} @ ${slice.price} •{' '}
                              {Number.isFinite(slice.change) && slice.change >= 0 ? '+' : ''}
                              {(Number.isFinite(slice.change) ? slice.change : 0).toFixed(1)}%
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold">
                            ${(slice.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </p>
                          <p className="text-xs text-slate-500">{Math.round(slice.valueShare * 100)}% allocation</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white border border-purple-100 rounded-2xl p-6 shadow-lg h-full">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-purple-700 font-semibold">
                  <BarChart3 className="w-5 h-5 text-purple-500" />
                  Position details
                </div>
              </div>

              {holdings.length === 0 ? (
                <p className="text-slate-600 text-sm">Save a portfolio to see details here.</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                  {holdings.map((holding, idx) => {
                    const value = (holding.price || 0) * (holding.quantity || 0)
                    const positive = (holding.change || 0) >= 0
                    return (
                      <div
                        key={holding.id || holding.name || idx}
                        className="border border-purple-100 bg-white rounded-xl p-4 flex justify-between items-center gap-4 shadow-sm"
                      >
                        <div>
                          <p className="text-lg font-semibold">{holding.name}</p>
                          <p className="text-xs text-slate-500">
                            {holding.quantity} shares · ${holding.price} each
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-700 mb-1">
                            Value: ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </p>
                          <div
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${
                              positive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
                            }`}
                          >
                            {positive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                            {positive ? '+' : ''}
                            {(holding.change ?? 0).toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default PortfolioPage
